import { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Paste-a-list bulk import for the watchlist. Users can paste any noisy blob
 * (CSV, space, newline, mixed) — the backend splits on non-symbol characters.
 * Shows per-symbol added / skipped / invalid results after submit.
 */
export default function WatchlistImportButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Paste some symbols first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/watchlist/import", { text });
      setResult(data);
      const a = data.added?.length || 0;
      const s = data.skipped?.length || 0;
      const i = data.invalid?.length || 0;
      if (a > 0 && i === 0) toast.success(`Added ${a} · skipped ${s}`);
      else if (a > 0 && i > 0) toast.warning(`Added ${a} · skipped ${s} · invalid ${i}`);
      else if (a === 0 && s > 0) toast.info(`All ${s} already on your watchlist`);
      else toast.error(`No valid symbols in ${i + s} tokens`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const reset = () => { setText(""); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          data-testid="watchlist-import-btn"
          variant="outline"
          className="bg-transparent border-zinc-700 hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest gap-1.5 h-9"
        >
          <UploadSimple size={12} weight="bold" /> Import
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 max-w-md text-white"
        data-testid="watchlist-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="mono">Import watchlist</DialogTitle>
          <DialogDescription className="mono text-xs text-zinc-500">
            Paste tickers — commas, spaces, or newlines. Up to 100 per batch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="AAPL, MSFT, NVDA&#10;TSLA GOOGL AMZN"
            rows={5}
            data-testid="watchlist-import-textarea"
            className="bg-zinc-900 border-zinc-800 mono text-sm resize-none"
          />

          {result && (
            <div className="border border-zinc-800 rounded-sm p-2 mono text-[11px] space-y-1" data-testid="watchlist-import-result">
              {result.added?.length > 0 && (
                <div>
                  <span className="text-[#00C805]">Added {result.added.length}:</span>{" "}
                  <span className="text-zinc-300">{result.added.join(", ")}</span>
                </div>
              )}
              {result.skipped?.length > 0 && (
                <div>
                  <span className="text-zinc-500">Skipped {result.skipped.length}:</span>{" "}
                  <span className="text-zinc-500">{result.skipped.join(", ")}</span>
                </div>
              )}
              {result.invalid?.length > 0 && (
                <div>
                  <span className="text-[#FF3B30]">Invalid {result.invalid.length}:</span>{" "}
                  <span className="text-zinc-500">{result.invalid.join(", ")}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={submit}
              disabled={busy || !text.trim()}
              data-testid="watchlist-import-submit"
              className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-sm mono text-[10px] uppercase tracking-widest disabled:opacity-50 gap-1.5"
            >
              {busy && <CircleNotch size={12} className="animate-spin" />}
              {busy ? "Importing…" : "Import"}
            </Button>
            {result && (
              <Button
                onClick={reset}
                variant="outline"
                data-testid="watchlist-import-reset"
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
